import { atelierHubUrl } from '@lib/env';
import { initials } from '@lib/initials';
import { IconExternal } from '@shared/ui';

import type { Operator } from '@lib/operator';

/**
 * Topbar. The identity on the right is the **real** Google account from Atelier's
 * shared session — visible proof to the employee that they are signed in as
 * themselves without ever having logged into this app.
 *
 * There is no sign-out here on purpose: Atelier owns the session, and clearing
 * `vv_session` from the Backoffice would sign the user out of every internal
 * tool while leaving the Google/Cognito session alive to silently sign them back
 * in. The app-switcher link sends them to the Hub instead.
 *
 * Everything here is functional. The mockup's search, tenant switcher,
 * notifications, help, and environment pill were removed rather than shipped
 * inert — each returns when it has something real behind it. In particular
 * there is no environment indicator because there is no environment dimension:
 * the AWS account the app runs in *is* the environment (ADR 0002), and today
 * only production exists.
 */
export function Topbar({ operator }: { operator: Operator }) {
    return (
        <div className="flex h-full items-center gap-4 px-5">
            <a
                href={atelierHubUrl()}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
            >
                <span className="size-4 rounded bg-gray-800" />
                Atelier Hub
                <IconExternal className="size-3.5 text-gray-400" />
            </a>

            <div className="flex-1" />

            <div className="flex items-center gap-2.5 border-l border-gray-200 pl-3">
                <div className="hidden text-right leading-tight lg:block">
                    <div className="text-[13px] font-semibold text-gray-900">{operator.name}</div>
                    <div className="text-[11px] text-gray-500">{operator.email}</div>
                </div>
                {/*
                 * Initials, not the Google `avatarUrl` the session carries: the
                 * mockup specifies initials, and rendering a remote avatar would
                 * make an internal admin tool fetch an image from a third-party
                 * host on every page. The URL stays on the operator type if a
                 * later design wants it.
                 */}
                <div
                    aria-hidden="true"
                    className="flex size-8 items-center justify-center rounded-full border border-brand-200 bg-brand-50 text-xs font-semibold text-brand-700"
                >
                    {initials(operator.name)}
                </div>
            </div>
        </div>
    );
}
